<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onActivated, onDeactivated, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { AVAILABLE_CUSTOM_ROLE_PERMISSIONS } from 'dashboard/constants/permissions';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import AccessProfiles from './AccessProfiles.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { prefetchAgentsPageData } from './agentsPageData';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

// Chrome da pagina conforme o mockup. "Perfis de acesso" ainda nao tem chave de
// i18n porque a tela e um prototipo visual; ao ligar no backend, mover para
// os arquivos de locale junto com o resto do texto de AccessProfiles.vue.
const PAGE_COPY = {
  title: 'Configurações',
  subtitle: t('AGENT_MGMT.DESCRIPTION'),
};
// Ver o mesmo gate em teams/Index.vue: com <keep-alive> o header teleportado
// sobrevive a saida da rota e apareceria na pagina de Equipes.
const isPageActive = ref(true);
onActivated(() => {
  isPageActive.value = true;
});
onDeactivated(() => {
  isPageActive.value = false;
});

const TABS = [
  { id: 'users', label: t('AGENT_MGMT.HEADER') },
  { id: 'profiles', label: 'Perfis de acesso' },
];
// Preserva a gestao de usuarios como fluxo principal; perfis de acesso seguem
// disponiveis na aba dedicada.
const activeTab = ref('users');

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const searchQuery = ref('');
const selectedAgentId = ref(null);

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);

const filteredAgentList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agentList.value;
  return picoSearch(agentList.value, query, ['name', 'email']);
});

const uiFlags = computed(() => getters['agents/getUIFlags'].value);

// So bloqueia a tela quando nao ha nada para mostrar: a revalidacao em segundo
// plano nao pode substituir a lista ja carregada por um spinner.
const isInitialLoading = computed(
  () => uiFlags.value.isFetching && !agentList.value.length
);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

// A guarda de rota ja deixou os dados prontos antes da tela montar; isso aqui e
// so a rede de seguranca para quem chegar sem passar por ela.
onMounted(() => {
  prefetchAgentsPageData();
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const getAgentTeams = agent => agent.teams || [];

const getAgentPhone = agent => agent.custom_attributes?.phone_number || '';

const getAgentDisplayName = agent => agent.display_name || '';

// Painel da direita: espelha a linha destacada na lista. Cai no primeiro da
// lista filtrada para o painel nunca ficar vazio enquanto houver usuarios.
const selectedAgent = computed(() => {
  const list = filteredAgentList.value;
  if (!list.length) return null;
  return list.find(agent => agent.id === selectedAgentId.value) || list[0];
});

const selectAgent = agent => {
  selectedAgentId.value = agent.id;
};

const isSelected = agent => selectedAgent.value?.id === agent.id;

// So faz sentido marcar/desmarcar quando a funcao e personalizada: ai o
// conjunto de permissoes e explicitamente um subconjunto dos disponiveis.
// Funcoes padrao (Administrador/Atendente) nao tem essa lista no backend.
const availablePermissions = AVAILABLE_CUSTOM_ROLE_PERMISSIONS;

const agentHasPermission = (agent, permission) =>
  getAgentRolePermissions(agent).includes(permission);

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsLayout
    class="agents-page"
    :is-loading="activeTab === 'users' && isInitialLoading"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="
      activeTab === 'users' && !isInitialLoading && !agentList.length
    "
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <Teleport
        v-if="isPageActive"
        defer
        to=".settings-wrapper__teams-header-outlet"
      >
        <header
          class="aceleres-agents-page__header acl-mobile-account-menu-host flex flex-shrink-0 items-center justify-between gap-4 px-0 py-4 transition-[padding] duration-200 ease-out max-md:-mx-10 max-md:px-4"
        >
          <div class="agents-page__header-content min-w-0 max-md:w-full">
            <h1
              class="agents-page__title truncate text-xl font-bold text-n-slate-12 max-md:flex-1 max-md:pr-[52px] max-md:text-center max-md:text-[25px] max-md:leading-7"
            >
              {{ PAGE_COPY.title }}
            </h1>
            <p
              class="agents-page__subtitle mt-1 text-sm text-n-slate-11 max-md:text-center"
            >
              {{ PAGE_COPY.subtitle }}
            </p>
          </div>
        </header>
      </Teleport>
    </template>
    <template #body>
      <div class="agents-page__tabs mb-3 mt-6 inline-flex items-center gap-2">
        <button
          v-for="tab in TABS"
          :key="tab.id"
          type="button"
          class="agents-page__tab inline-flex h-[40px] items-center justify-center rounded-full border px-4 text-body-main transition-colors"
          :class="
            activeTab === tab.id
              ? 'border-n-brand/40 bg-n-brand/10 font-medium text-n-blue-11'
              : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="activeTab = tab.id"
        >
          {{ tab.label }}
        </button>
      </div>
      <AccessProfiles
        v-if="activeTab === 'profiles'"
        :agents="agentList"
        class="agents-page__profiles"
      />
      <div
        v-else
        class="agents-page__users-grid grid items-stretch gap-4 max-md:-mx-2 max-md:w-[calc(100%+1rem)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]"
      >
        <section
          class="agents-page__users-list-card flex min-h-0 flex-col gap-4 overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 p-5 max-md:grid max-md:grid-cols-[minmax(0,1fr)_auto] max-md:items-center"
        >
          <h2
            class="agents-page__card-title text-base font-semibold text-n-slate-12 max-md:col-start-1 max-md:row-start-1"
          >
            {{ $t('AGENT_MGMT.HEADER') }}
          </h2>

          <div
            class="agents-page__toolbar flex items-center gap-3 max-md:contents"
          >
            <div
              class="agents-page__search-wrapper relative min-w-0 flex-1 max-md:col-span-2 max-md:row-start-2 max-md:w-full"
            >
              <Icon
                icon="i-lucide-search"
                class="agents-page__search-icon pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-10 ltr:left-3 rtl:right-3"
              />
              <input
                v-model="searchQuery"
                type="search"
                :placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
                class="agents-page__search !m-0 h-10 w-full rounded-xl border border-n-weak bg-n-solid-1 text-body-main text-n-slate-12 outline-none placeholder:text-n-slate-10 focus:border-n-brand ltr:!pl-9 ltr:!pr-3 rtl:!pr-9 rtl:!pl-3"
              />
            </div>
            <button
              type="button"
              class="agents-page__add-user inline-flex h-10 flex-shrink-0 items-center gap-2 rounded-xl border border-solid border-n-brand bg-n-brand/5 px-4 text-body-main font-medium text-n-blue-11 transition-colors hover:bg-n-brand/10 max-md:col-start-2 max-md:row-start-1"
              @click="openAddPopup"
            >
              <Icon icon="i-lucide-plus" class="size-4" />
              {{ $t('AGENT_MGMT.HEADER_BTN_TXT') }}
            </button>
          </div>

          <span
            v-if="!filteredAgentList.length && searchQuery"
            class="agents-page__no-results flex items-center justify-center py-16 text-center text-body-main text-n-slate-11 max-md:col-span-2"
          >
            {{ $t('AGENT_MGMT.NO_RESULTS') }}
          </span>

          <div
            v-else
            class="agents-page__users-table flex min-h-0 flex-1 flex-col max-md:col-span-2"
          >
            <div
              class="agents-page__users-table-header grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-3 border-b border-n-weak px-3 pb-2"
            >
              <span
                class="agents-page__users-table-heading agents-page__users-table-heading--name text-body-mini font-medium text-n-slate-11"
              >
                {{ $t('AGENT_MGMT.LIST.NAME') }}
              </span>
              <span
                class="agents-page__users-table-heading agents-page__users-table-heading--teams text-center text-body-mini font-medium text-n-slate-11"
              >
                {{ $t('AGENT_MGMT.EDIT.TEAMS.TITLE') }}
              </span>
              <span
                class="agents-page__users-table-heading agents-page__users-table-heading--role text-center text-body-mini font-medium text-n-slate-11"
              >
                {{ $t('AGENT_MGMT.FORM.AGENT_TYPE.LABEL') }}
              </span>
            </div>

            <div
              class="agents-page__users-table-scroll relative min-h-0 flex-1 max-md:flex-none"
            >
              <div
                class="agents-page__users-table-body no-scrollbar absolute inset-0 overflow-y-auto max-md:static max-md:max-h-96"
              >
                <!-- div em vez de button: o Avatar injeta um <div> no badge de
              status, e <div> dentro de <button> e HTML invalido. -->
                <div
                  v-for="agent in filteredAgentList"
                  :key="agent.email"
                  role="button"
                  tabindex="0"
                  :aria-pressed="isSelected(agent)"
                  class="agents-page__user-row grid cursor-pointer grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-3 border-b border-n-weak px-3 py-3 text-left transition-colors ltr:border-l-2 rtl:border-r-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                  :class="
                    isSelected(agent)
                      ? 'border-l-n-brand rtl:border-r-n-brand bg-n-brand/5'
                      : 'ltr:border-l-transparent rtl:border-r-transparent hover:bg-n-alpha-1'
                  "
                  @click="selectAgent(agent)"
                  @keydown.enter.prevent="selectAgent(agent)"
                  @keydown.space.prevent="selectAgent(agent)"
                >
                  <span
                    class="agents-page__user-identity flex min-w-0 items-center gap-2.5"
                  >
                    <Avatar
                      class="agents-page__user-avatar"
                      rounded-full
                      :src="agent.thumbnail"
                      :name="agent.name"
                      :size="40"
                    />
                    <span class="agents-page__user-name-wrap min-w-0 truncate">
                      <span
                        class="agents-page__user-name block truncate text-body-main text-n-slate-12 capitalize"
                      >
                        {{ agent.name }}
                      </span>
                    </span>
                  </span>

                  <span
                    class="agents-page__user-teams truncate text-center text-body-mini text-n-slate-11"
                  >
                    <template v-if="getAgentTeams(agent).length">
                      {{
                        getAgentTeams(agent)
                          .map(team => team.name)
                          .join(', ')
                      }}
                    </template>
                    <template v-else>
                      {{ $t('AGENT_MGMT.LIST.NO_TEAMS') }}
                    </template>
                  </span>

                  <span
                    class="agents-page__user-role truncate text-center text-body-main font-medium text-n-slate-12"
                  >
                    {{ getAgentRoleName(agent) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="selectedAgent"
          class="agents-page__user-details-card scrollbar-thin flex min-h-0 flex-col gap-4 overflow-y-auto rounded-2xl border border-n-weak bg-n-solid-1 p-5"
        >
          <h2
            class="agents-page__card-title agents-page__details-title text-base font-semibold text-n-slate-12"
          >
            {{ $t('AGENT_MGMT.EDIT.TITLE') }}
          </h2>

          <div
            class="agents-page__details-field agents-page__details-field--name flex flex-col gap-1.5"
          >
            <span
              class="agents-page__details-label text-body-mini font-medium text-n-slate-11"
            >
              {{ $t('AGENT_MGMT.FORM.NAME.LABEL') }}
            </span>
            <div
              class="agents-page__details-value agents-page__details-value--name flex h-12 min-w-0 items-center gap-2.5 rounded-lg border border-n-weak bg-n-alpha-1 px-3"
            >
              <Avatar
                class="agents-page__details-avatar"
                rounded-full
                :src="selectedAgent.thumbnail"
                :name="selectedAgent.name"
                :size="40"
              />
              <span
                class="agents-page__details-name truncate text-body-main !leading-[1.9] text-n-slate-12 capitalize"
              >
                {{ selectedAgent.name }}
              </span>
            </div>
          </div>

          <div
            class="agents-page__details-field agents-page__details-field--email flex flex-col gap-1.5"
          >
            <span
              class="agents-page__details-label text-body-mini font-medium text-n-slate-11"
            >
              {{ $t('AGENT_MGMT.FORM.EMAIL.LABEL') }}
            </span>
            <div
              class="agents-page__details-value agents-page__details-value--email flex h-12 items-center truncate rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-body-main !leading-[1.9] text-n-slate-12"
            >
              {{ selectedAgent.email }}
            </div>
          </div>

          <div class="agents-page__details-grid grid gap-4 sm:grid-cols-2">
            <div
              class="agents-page__details-field agents-page__details-field--role flex flex-col gap-1.5"
            >
              <span
                class="agents-page__details-label text-body-mini font-medium text-n-slate-11"
              >
                {{ $t('AGENT_MGMT.FORM.AGENT_TYPE.LABEL') }}
              </span>
              <div
                class="agents-page__details-value agents-page__details-value--role flex h-12 items-center gap-1.5 truncate rounded-lg border border-n-brand/30 bg-n-brand/5 px-3 text-body-main !leading-[1.9] font-medium text-n-blue-11"
              >
                <Icon
                  icon="i-lucide-user-round-cog"
                  class="agents-page__details-role-icon size-3.5 flex-shrink-0"
                />
                {{ getAgentRoleName(selectedAgent) }}
              </div>
            </div>

            <div
              class="agents-page__details-field agents-page__details-field--phone flex flex-col gap-1.5"
            >
              <span
                class="agents-page__details-label text-body-mini font-medium text-n-slate-11"
              >
                {{ $t('AGENT_MGMT.FORM.PHONE.LABEL') }}
              </span>
              <div
                class="agents-page__details-value agents-page__details-value--phone flex h-12 items-center truncate rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-body-main !leading-[1.9] text-n-slate-12"
              >
                {{
                  getAgentPhone(selectedAgent) ||
                  $t('AGENT_MGMT.FORM.EMPTY_VALUE')
                }}
              </div>
            </div>

            <div
              class="agents-page__details-field agents-page__details-field--display-name flex flex-col gap-1.5 sm:col-span-2"
            >
              <span
                class="agents-page__details-label text-body-mini font-medium text-n-slate-11"
              >
                {{ $t('AGENT_MGMT.FORM.DISPLAY_NAME.LABEL') }}
              </span>
              <div
                class="agents-page__details-value agents-page__details-value--display-name flex h-12 items-center truncate rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-body-main !leading-[1.9] text-n-slate-12"
              >
                {{
                  getAgentDisplayName(selectedAgent) ||
                  $t('AGENT_MGMT.FORM.EMPTY_VALUE')
                }}
              </div>
            </div>
          </div>

          <div class="agents-page__details-grid grid gap-4 sm:grid-cols-2">
            <div
              class="agents-page__details-field agents-page__details-field--teams flex flex-col gap-1.5"
            >
              <span
                class="agents-page__details-label text-body-mini font-medium text-n-slate-11"
              >
                {{ $t('AGENT_MGMT.FORM.TEAMS.TITLE') }}
              </span>
              <div
                class="agents-page__details-teams flex min-h-[8rem] flex-col rounded-lg border border-n-weak"
              >
                <span
                  v-if="!getAgentTeams(selectedAgent).length"
                  class="agents-page__details-teams-empty px-3 py-2 text-body-mini text-n-slate-10"
                >
                  {{ $t('AGENT_MGMT.LIST.NO_TEAMS') }}
                </span>
                <span
                  v-for="team in getAgentTeams(selectedAgent)"
                  :key="team.id"
                  class="agents-page__details-team-row flex items-center border-b border-n-weak px-3 py-2 last:border-b-0"
                >
                  <span
                    class="agents-page__details-team-name min-w-0 break-words text-body-main text-n-slate-12"
                  >
                    {{ team.name }}
                  </span>
                </span>
              </div>
            </div>

            <div
              class="agents-page__details-field agents-page__details-field--permissions flex flex-col gap-1.5"
            >
              <span
                class="agents-page__details-label text-body-mini font-medium text-n-slate-11"
              >
                {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL') }}
              </span>
              <div
                class="agents-page__details-permissions flex min-h-[8rem] flex-col rounded-lg border border-n-weak"
              >
                <template v-if="selectedAgent.custom_role_id">
                  <span
                    v-for="permission in availablePermissions"
                    :key="permission"
                    class="agents-page__details-permission-row flex items-center justify-between gap-2 border-b border-n-weak px-3 py-2 last:border-b-0"
                  >
                    <span
                      class="agents-page__details-permission-name text-body-mini text-n-slate-11"
                    >
                      {{
                        $t(
                          `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                        )
                      }}
                    </span>
                    <Icon
                      v-if="agentHasPermission(selectedAgent, permission)"
                      icon="i-lucide-square-check"
                      class="agents-page__details-permission-icon agents-page__details-permission-icon--on size-4 flex-shrink-0 text-n-brand"
                    />
                    <Icon
                      v-else
                      icon="i-lucide-square"
                      class="agents-page__details-permission-icon agents-page__details-permission-icon--off size-4 flex-shrink-0 text-n-slate-8"
                    />
                  </span>
                </template>
                <span
                  v-else
                  class="agents-page__details-permissions-empty px-3 py-2 text-body-mini text-n-slate-10"
                >
                  {{ getAgentRoleName(selectedAgent) }}
                </span>
              </div>
            </div>
          </div>

          <div
            class="agents-page__user-actions flex items-center justify-end gap-3 pt-1"
          >
            <Button
              v-if="showDeleteAction(selectedAgent)"
              variant="outline"
              color="ruby"
              size="md"
              class="agents-page__delete-user w-24 rounded-xl bg-n-ruby-9/10"
              :label="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
              :is-loading="loading[selectedAgent.id]"
              @click="openDeletePopup(selectedAgent)"
            />
            <Button
              v-if="showEditAction(selectedAgent)"
              variant="outline"
              color="blue"
              size="md"
              class="agents-page__edit-user w-24 rounded-xl bg-n-brand/10"
              :label="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
              @click="openEditPopup(selectedAgent)"
            />
          </div>
        </section>
      </div>
    </template>

    <AddAgent v-if="showAddPopup" @close="hideAddPopup" />

    <EditAgent
      v-if="showEditPopup"
      :id="currentAgent.id"
      :name="currentAgent.name"
      :provider="currentAgent.provider"
      :type="currentAgent.role"
      :email="currentAgent.email"
      :thumbnail="currentAgent.thumbnail || ''"
      :display-name="currentAgent.display_name || ''"
      :phone-number="currentAgent.custom_attributes?.phone_number || ''"
      :availability="currentAgent.availability_status"
      :custom-role-id="currentAgent.custom_role_id"
      :teams="currentAgent.teams || []"
      :sessions="currentAgent.sessions || []"
      :active-sessions-count="currentAgent.active_sessions_count || 0"
      :current-sign-in-at="currentAgent.current_sign_in_at"
      :current-sign-in-ip="currentAgent.current_sign_in_ip"
      :last-sign-in-at="currentAgent.last_sign_in_at"
      :last-sign-in-ip="currentAgent.last_sign_in_ip"
      @close="hideEditPopup"
    />

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      modal-container-class="agents-page__delete-modal max-md:mx-4 max-md:max-h-[calc(100dvh-2rem)] max-md:w-[calc(100%-2rem)] max-md:rounded-2xl"
      header-class="agents-page__delete-modal-header max-md:px-5 max-md:pb-0 max-md:pr-12 max-md:pt-6"
      actions-class="agents-page__delete-modal-actions max-md:flex-col-reverse max-md:items-stretch max-md:px-5 max-md:pb-5 max-md:pt-6 max-md:[&>button]:h-auto max-md:[&>button]:min-h-10 max-md:[&>button]:w-full max-md:[&>button]:whitespace-normal"
    />
  </SettingsLayout>
</template>

<style scoped>
/* O container de settings desta rota recebe `scrollbar-thin` (SettingsWrapper),
   e essa utilitaria casa tambem os descendentes (`.scrollbar-thin *`). Como ela
   tem a mesma especificidade de `.no-scrollbar` e vem depois no CSS gerado, a
   barra voltava a aparecer aqui dentro. O seletor abaixo, com o atributo de
   escopo, ganha em especificidade e some com a barra so nesta lista. */
.agents-page__users-table-body {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.agents-page__users-table-body::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}
</style>
